import React, { useEffect, useState } from 'react';
import { Form, Row } from 'react-bootstrap';

import numberFormatter from '../../../common/numberFormatter';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import {
  RegisterStatistics,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import ValidationAlert from '../../../app/components/ValidationAlert';
import UserSelector from '../Selects/UserSelector';
import { Role } from '../../../common/spdCore/authorization/roles';

import {
  buildCurrentMonthRange,
  DateRangeState,
  toDateEnd,
  toDateStart,
} from './dateRangeUtils';
import {
  DateRangeFilterApplied,
  SuperAdminDateRangeFilter,
} from './SuperAdminDateRangeFilter';
import { SuperAdminCardMenu } from './SuperAdminCardMenu';

const COUNT_IN_ROW = 3;

export const SuperAdminRegisterTab: React.FC = () => {
  const spd = useSpdCore();
  const [dataStatistic, setDataStatistic] = useState<RegisterStatistics>();
  const [errors, setErrors] = useState<string[]>([]);
  const [filters, setFilters] = useState<DateRangeState>(
    buildCurrentMonthRange,
  );
  const [applied, setApplied] = useState<DateRangeFilterApplied>(
    buildCurrentMonthRange,
  );
  const [userUid, setUserUid] = useState<string | undefined>(undefined);
  const [appliedUserUid, setAppliedUserUid] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    setErrors([]);
    spd.reports
      .reportRegisterStatisticsSPDByMonthsCounts(
        toDateStart(applied.dateFrom),
        toDateEnd(applied.dateTo),
        appliedUserUid,
        applied.companyId,
      )
      .then(data => setDataStatistic(data))
      .catch(async error => {
        setDataStatistic(undefined);
        console.error('Error during report statistic SPD', error);
        setErrors(await responseErrors(error as ResponseError));
      });
  }, [spd.reports, applied, appliedUserUid]);

  return (
    <>
      <h4 className="m-0 mb-4">Statystyki rejestrów i konsultacji</h4>
      <SuperAdminDateRangeFilter
        filters={filters}
        setFilters={setFilters}
        applyLabel="Pokaż"
        withCompany={true}
        onApply={a => {
          setApplied(a);
          setAppliedUserUid(userUid);
        }}
        extraFilter={
          <Form.Group controlId="registerStatsUser">
            <Form.Label>Lekarz / psycholog</Form.Label>
            <UserSelector
              roles={[
                Role.MedicalSpecialist,
                Role.MedicalAssessor,
                Role.PsychologistSpecialist,
                Role.PsychologistAssessor,
                Role.Laryngologist,
                Role.Psychiatrist,
                Role.Neurologist,
                Role.Ophthalmologist,
              ]}
              onChange={option => {
                setUserUid(option?.value);
              }}
              clearable={true}
            />
          </Form.Group>
        }
      />
      <ValidationAlert
        show={errors.length > 0}
        errors={errors}
        className="mt-3"
      />
      <div className="mb-4">
        <h5 className="mb-3">Rejestry</h5>
        {dataStatistic?.registers &&
          Array.from(
            {
              length: Math.ceil(dataStatistic.registers.length / COUNT_IN_ROW),
            },
            (_, rowIndex) => (
              <Row
                xs={1}
                md={COUNT_IN_ROW}
                className="g-4 mb-4"
                key={`row-${rowIndex}`}
              >
                {dataStatistic.registers
                  .slice(
                    rowIndex * COUNT_IN_ROW,
                    rowIndex * COUNT_IN_ROW + COUNT_IN_ROW,
                  )
                  .map((item, index) => (
                    <SuperAdminCardMenu
                      key={`register-${rowIndex}-${index}`}
                      title={item.label}
                      text={numberFormatter(item.count)}
                      classNameText={'stat-value-purple'}
                    />
                  ))}
              </Row>
            ),
          )}
      </div>

      <div>
        <h5 className="mb-3">Konsultacje</h5>
        {dataStatistic?.consultations &&
          Array.from(
            {
              length: Math.ceil(
                dataStatistic.consultations.length / COUNT_IN_ROW,
              ),
            },
            (_, rowIndex) => (
              <Row
                xs={1}
                md={COUNT_IN_ROW}
                className="g-4 mb-4"
                key={`row-${rowIndex}`}
              >
                {dataStatistic.consultations
                  .slice(
                    rowIndex * COUNT_IN_ROW,
                    rowIndex * COUNT_IN_ROW + COUNT_IN_ROW,
                  )
                  .map((item, index) => (
                    <SuperAdminCardMenu
                      key={`consultations-${rowIndex}-${index}`}
                      title={item.label}
                      text={numberFormatter(item.count)}
                      classNameText={'stat-value-dark-purple'}
                    />
                  ))}
              </Row>
            ),
          )}
      </div>
    </>
  );
};
